import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button, Box, Stack, TextField, Typography
} from "@mui/material";
import axios from "axios";
import { baseURL } from "../../../api";
import CustomAlert from "../../../component/AlertComponent/Alert";

const isDateType = type => ["DATE", "DATETIME", "CREATEDATETIME", "UPDATEDATETIME"]
  .includes(String(type || "").toUpperCase());
const dateInputType = type => String(type || "").toUpperCase() === "DATE"
  ? "date"
  : isDateType(type) ? "datetime-local" : "text";

export default function ReportPreview({ open, onClose, template, reportId, authHeaders = {} }) {
  const columnar = (template?.reportKind || template?.dataSource?.type) === "COLUMNAR";
  const [filters, setFilters] = useState({});
  const [html, setHtml] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open) {
      setHtml("");
      setFilters({});
    }
  }, [open]);

  const setFilterValue = (path, value) =>
    setFilters(current => ({ ...current, [path]: value }));

  const generate = async () => {
    if (!reportId) {
      CustomAlert("warning", "Save the COLUMNAR report before generating a data preview.");
      return;
    }
    setWorking(true);
    try {
      const response = await axios.post(
        `${baseURL}/report/columnar/${reportId}/preview`,
        { filters },
        { headers: authHeaders, responseType: "text" }
      );
      setHtml(response.data || "");
    } catch (error) {
      CustomAlert("error", error?.response?.data?.metadata?.msg || "Unable to generate report preview.");
    } finally {
      setWorking(false);
    }
  };

  const exportReport = async format => {
    setWorking(true);
    try {
      const response = await axios.post(
        `${baseURL}/report/columnar/${format}/${reportId}`,
        { data: { mainrecord: filters, tablerecords: [] } },
        { headers: authHeaders, responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      if (format === "pdf") {
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = `${template?.reportName || `report-${reportId}`}.${format}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      CustomAlert("error", "Unable to export the report.");
    } finally {
      setWorking(false);
    }
  };

  if (columnar) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>{template?.reportName || "Columnar Report"}</DialogTitle>
        <DialogContent dividers>
          {!html ? (
            <Stack spacing={2}>
              <Typography variant="subtitle1">Report Filters</Typography>
              {(template?.filters || []).length === 0 && (
                <Typography color="text.secondary">
                  This report has no filters. Click Generate Report to continue.
                </Typography>
              )}
              {(template?.filters || []).map(filter => {
                const path = filter.fieldPath || filter.field;
                const range = filter.defaultOperator === "BETWEEN" || filter.operatorMode === "RANGE";
                if (range) {
                  const value = filters[path] || {};
                  return (
                    <Stack key={path} direction="row" spacing={2}>
                      <TextField label={`${filter.label} From`}
                        type={dateInputType(filter.dataType)}
                        value={value.from || ""}
                        onChange={event => setFilterValue(path, { ...value, from: event.target.value })}
                        InputLabelProps={{ shrink: true }} required={Boolean(filter.required)} fullWidth />
                      <TextField label={`${filter.label} To`}
                        type={dateInputType(filter.dataType)}
                        value={value.to || ""}
                        onChange={event => setFilterValue(path, { ...value, to: event.target.value })}
                        InputLabelProps={{ shrink: true }} required={Boolean(filter.required)} fullWidth />
                    </Stack>
                  );
                }
                return (
                  <TextField key={path} label={filter.label}
                    type={dateInputType(filter.dataType)}
                    value={filters[path] || ""}
                    onChange={event => setFilterValue(path, event.target.value)}
                    InputLabelProps={isDateType(filter.dataType) ? { shrink: true } : undefined}
                    required={Boolean(filter.required)} fullWidth />
                );
              })}
            </Stack>
          ) : (
            <Box component="iframe" title="Columnar HTML Report" srcDoc={html}
              sx={{ width: "100%", minHeight: "70vh", border: 0, bgcolor: "white" }} />
          )}
        </DialogContent>
        <DialogActions>
          {html && <Button onClick={() => setHtml("")}>Change Filters</Button>}
          {html && <Button onClick={generate} disabled={working}>Refresh</Button>}
          {html && <Button onClick={() => exportReport("pdf")} disabled={working}>Print / PDF</Button>}
          {html && <Button onClick={() => exportReport("csv")} disabled={working}>Export CSV</Button>}
          {!html && <Button variant="contained" onClick={generate} disabled={working}>Generate Report</Button>}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>HTML Preview</DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            position: "relative",
            width: template.page.width,
            height: template.page.height,
            bgcolor: "white",
            mx: "auto",
            boxShadow: 2
          }}
        >
          {template.elements.map(element => (
            <Box
              key={element.id}
              sx={{
                position: "absolute",
                left: element.x,
                top: element.y,
                width: element.width,
                height: element.height,
                fontSize: element.style?.fontSize || 12,
                fontWeight: element.style?.bold ? "bold" : "normal"
              }}
            >
              {element.type === "field"
                ? `{${element.fieldPath || element.field}}`
                : element.text}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
